import * as rrweb from 'rrweb';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { debounceTime, share, tap } from 'rxjs/operators';

import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, LoadingController, ModalController, Platform, ToastController, ToastOptions } from '@ionic/angular';
import * as Sentry from '@sentry/angular';
import { CacheService } from '@shared/services';
import { EmployeeService } from '@shared/services/api/employee-data.service';
import { PricingDataService } from '@shared/services/api/pricing-data.service';
import { ProductsDataService } from '@shared/services/api/products-data.service';
import { ConfigService } from '@shared/services/core/config.service';
import { CompanyService } from '@shared/state/company/company.service';
import { EmployeesRepository } from '@shared/state/employees/employees.repository';
import { ActiveReservationRepository } from '@shared/state/reservations/active-reservation.repository';
import { ReservationService } from '@shared/state/reservations/reservation.service';

import packageJson from '../../../package.json';
import { AppService } from '../app.service';
import { InjectorService } from '../injector.service';
import { MemoryService } from '../shared/services/core/memory.service';
import { RemoteConfigService } from '../shared/services/core/remote-config.service';

export const retryCount = 3;
export const retryWaitMilliSeconds = 1000;

export class BasePage {
	static hasRegistered = false;
	public loadingController: LoadingController;
	public modalController: ModalController;
	public injectorService: InjectorService;
	public toggleMode: boolean = false;
	public message: string = '';
	public cache: CacheService;
	public filesuffix = new Date().getTime();
	public interval;
	public location: string = '';
	public baseRouter: Router;
	public ac: ActivatedRoute;
	private baseurl: string = '';
	public _loading = null;
	public toastController: ToastController;
	public alertController: AlertController;
	public env: ConfigService;
	public subscriptions: Subscription[] = [];
	public remoteConfig: RemoteConfigService;
	public disableLogin: boolean = false;
	set basePage(page) {
		this.baseurl = page;
	}
	get childUrlSnapshot() {
		return this.ac.children[0].children[0].snapshot;
	}

	public elfStatus = self => {
		return function <T>(source: Observable<T>) {
			return source.pipe(debounceTime(250), tap(this.modelLoading.bind(self)));
		}.bind(self);
	};
	private readonly _companyService: CompanyService;
	private readonly _productsService: ProductsDataService;
	private readonly _pricingService: PricingDataService;
	private readonly _reservationService: ReservationService;
	private readonly _employeesRepository: EmployeesRepository;
	private readonly _reservationRepository: ActiveReservationRepository;
	protected readonly _platform: Platform;
	private readonly _appService: AppService;
	private readonly _memoryService: MemoryService;

	private readonly _employeeService: EmployeeService;
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor() {
		this.loadingController = InjectorService.injector.get(LoadingController);
		this.modalController = InjectorService.injector.get(ModalController);
		this.cache = InjectorService.injector.get(CacheService);
		this.baseRouter = InjectorService.injector.get(Router);
		this.ac = InjectorService.injector.get(ActivatedRoute);
		this.toastController = InjectorService.injector.get(ToastController);
		this.alertController = InjectorService.injector.get(AlertController);
		this._companyService = InjectorService.injector.get(CompanyService);
		this._productsService = InjectorService.injector.get(ProductsDataService);
		this._pricingService = InjectorService.injector.get(PricingDataService);
		this._reservationService = InjectorService.injector.get(ReservationService);
		this._employeesRepository = InjectorService.injector.get(EmployeesRepository);
		this._reservationRepository = InjectorService.injector.get(ActiveReservationRepository);
		this._platform = InjectorService.injector.get(Platform);
		this.env = InjectorService.injector.get(ConfigService);
		this.remoteConfig = InjectorService.injector.get(RemoteConfigService);
		this._appService = InjectorService.injector.get(AppService);
		this._memoryService = InjectorService.injector.get(MemoryService);
		this._employeeService = InjectorService.injector.get(EmployeeService);

		if (!BasePage.hasRegistered) {
			this._platform.resume.subscribe(async e => {
				const isLoggedIn = this.cache.get('loggedin');
				if (isLoggedIn) {
					await this.refreshDependencies();
				}
				if (this._platform.is('hybrid')) {
					this.checkAppVersion();
					this.env.setSentry({
						sentryDSN: this.env.get('sentryDSN'),
						env: this.env.get('env'),
					});
				}
			});
			window.addEventListener(
				'admin:refresh',
				() => {
					this.refreshDependencies();
				},
				false
			);
		}
		BasePage.hasRegistered = true;
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - jnject
	 **-------------------------------------------------------------------------------------
	 */
	inject(token) {
		return InjectorService.injector.get(token);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - checkAppVersion
	 **-------------------------------------------------------------------------------------
	 */
	public checkAppVersion() {
		Promise.all([
			this.remoteConfig.getValueByKey('target_app_version'),
			this.remoteConfig.getValueByKey('force_app_update', 'boolean'),
			this.remoteConfig.getValueByKey('notify_app_update', 'boolean'),
		]).then(async ([targetAppVersion, forceUpdate, notifyUpdate]) => {
			if (targetAppVersion !== packageJson.version) {
				if (notifyUpdate && !forceUpdate) {
					await this.presentToast({
						header: 'New vQuipOS Update',
						message: 'A new version of vQuipOS has been released to the App Store, please update your app to access our newest features.',
						color: 'warning',
						duration: 5001,
					});
					return;
				}
				if (forceUpdate && !notifyUpdate) {
					this.disableLogin = true;
					await this.presentToast({
						header: 'Version Mismatch',
						message: 'You are using an outdated version of vQuipOS! Please update to the newest version in the App Store.',
						color: 'danger',
						duration: 5000,
					});
					this._appService.logout();
				}
				return;
			}
			this.disableLogin = false;
		});
	}
	/*
	/f
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - navigate
	 **-------------------------------------------------------------------------------------
	 */
	public async navigateByUrl(url, options = {}) {
		this.baseRouter.navigateByUrl(`${url}`, options);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getErrorMessage
	 **-------------------------------------------------------------------------------------
	 */
	public isFieldInvalid(form: FormGroup, field) {
		if (form && form.get(field).touched && form.get(field).hasError('required')) {
			return true;
		}
		if (form && form.get(field).touched && form.get(field).hasError('email')) {
			return true;
		}

		if (form && form.get(field).touched && form.get(field).hasError('pattern')) {
			return true;
		}
		return false;
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getErrorMessage
	 **-------------------------------------------------------------------------------------
	 */
	public getErrorMessage(form, field) {
		if (form && form.get(field).touched && form.get(field).hasError('required')) {
			return 'You must enter a value';
		}
		if (form && form.get(field).hasError('email')) {
			return 'You must enter a valid email';
		}
		if (form && form.get(field).hasError('maxlength')) {
			return 'You must enter a valid pattern';
		}
		if (form && form.get(field).hasError('pattern')) {
			return `You must enter a valid ${field}`;
		}
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - presentLoading
	 **-------------------------------------------------------------------------------------
	 */
	async presentLoading(message = 'Please Wait', duration = 45000) {
		if (this._loading) return;
		this._loading = await this.loadingController.create({
			message,
			duration,
		});
		await this._loading.present();
		this._loading?.onDidDismiss().then(async ({ data }) => {
			if (!data && duration === 30000) {
				const alert = await this.alertController.create({
					header: 'Long Request',
					message: 'The request is taking longer than expected due to your weak cellular strength, you can wait or go back to schedule',
					buttons: [
						{
							text: 'Ignore',
							handler: () => {
								this.dismissAllLoaders();
								return;
							},
						},
						{
							text: 'Back To Schedule',
							handler: () => {
								this.baseRouter.navigateByUrl('/app/tabs/schedule');
							},
						},
						{
							text: 'Keep Waiting',
							handler: async () => {
								this._loading = null;
								this.presentLoading(message);
							},
						},
					],
				});
				await alert.present();
			}
		});
		return this._loading;
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - prsentToast
	 **-------------------------------------------------------------------------------------
	 */
	public async presentToast({
		header,
		message,
		color = 'success',
		duration = 2000,
		position = 'bottom',
		redirect = '',
	}: {
		header: string;
		message: string;
		color?: ToastOptions['color'];
		duration?: number;
		position?: ToastOptions['position'];
		redirect?: string;
	}) {
		const toast = await this.toastController.create({
			header,
			color,
			keyboardClose: true,
			duration,
			message,
			position,
			buttons: [
				{
					text: 'Done',
					role: 'cancel',
					handler: () => {},
				},
			],
		});
		await toast.present();
		if (redirect.length) this.navigateByUrl(redirect);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - closeLoading
	 **-------------------------------------------------------------------------------------
	 */
	async closeLoading() {
		try {
			this._loading.dismiss(true);
		} catch (e) {}
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - onMessageCleared
	 **-------------------------------------------------------------------------------------
	 */
	public onMessageCleared(): void {
		this.message = '';
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - cancelModal
	 **-------------------------------------------------------------------------------------
	 */
	cancelModal() {
		this.modalController.dismiss(null, 'cancel');
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	async dismissAllLoaders() {
		setTimeout(async () => {
			if (this._loading?.dismiss) {
				await this._loading.dismiss(true);
				this._loading = null;
				return;
			}
			let topLoader = await this.loadingController.getTop();
			while (topLoader) {
				if (!(await topLoader.dismiss(true))) {
					return;
				}
				topLoader = await this.loadingController.getTop();
			}
		}, 500);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - record
	 **-------------------------------------------------------------------------------------
	 */
	public async record(type) {
		try {
			const reservation = await firstValueFrom(this._reservationRepository.selectActiveLaunch$);
			const filesuffix = this.filesuffix;
			const env = this.env;
			let events = [];
			rrweb.record({
				emit(event) {
					events.push(event);
				},
			});
			function save() {
				if (!events.length) return;
				const body = JSON.stringify({
					events,
					type,
					reservation: {
						company_id: reservation.registration.company.company_id,
						reservation_id: reservation.reservation_id,
					},
					filesuffix,
				});
				events = [];
				fetch(`${env.get('storageEndPoint')}/record`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'x-api-key': env.get('apiKey'),
						env: env.get('env'),
					},
					body,
				});
			}
			this.interval = setInterval(save, 20 * 1000);
		} catch (e) {}
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - play
	 **-------------------------------------------------------------------------------------
	 */
	public stopRecording(): void {
		try {
			setTimeout(() => {
				const highestTimeoutId = setTimeout(';');
				for (let i = 0; i < highestTimeoutId; i++) clearTimeout(i);
			}, 20 * 1000);
		} catch (e) {}
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - presentAlert
	 **-------------------------------------------------------------------------------------
	 */
	async alert({
		header = '',
		subHeader = '',
		buttons = ['Ok'],
		message,
	}: {
		header?: string;
		subHeader?: string;
		buttons?: any[];
		message: string;
	}) {
		const alert = await this.alertController.create({
			header,
			subHeader,
			message,
			buttons,
			backdropDismiss: false,
		});

		await alert.present();
		return alert;
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - modelLoading
	 **-------------------------------------------------------------------------------------
	 */
	public modelLoading(i: any): void {
		i.value == 'pending' ? this.presentLoading() : this.closeLoading();
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - redirectToSame
	 **-------------------------------------------------------------------------------------
	 */
	redirectTo(uri: string) {}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - refreshDependencies
	 **-------------------------------------------------------------------------------------
	 */
	public async refreshDependencies(companyId = null) {
		try {
			const company_id = companyId ? companyId : await this.cache.get('secure:company_id');
			this._companyService.get().pipe(share()).subscribe();
			this._companyService.getCompanyAddOns(company_id).pipe(share()).subscribe();
			this._employeeService.getList(company_id).pipe(share()).subscribe();
			this._companyService.getCompanyFeatures(company_id).pipe(share()).subscribe();
			this._pricingService.getPricingInventoryTypesForCompany(company_id, true).pipe(share()).subscribe();
			this._productsService.getList(true, company_id, true).pipe(share()).subscribe();
			this._productsService.getProductTypes(company_id, true).pipe(share()).subscribe();
		} catch (e) {
			console.log(e);
			Sentry.captureMessage('Error Refreshing base dependencies.');
			Sentry.captureException(e);
		}
	}

	public refreshCompanyData() {
		return this._companyService.get();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - launch
	 **-------------------------------------------------------------------------------------
	 */
	public async chooseDockhandForReservation(reservationId) {
		const employees = this._employeesRepository.getAll();
		const alert = await this.alertController.create({
			header: 'Start Checkin',
			subHeader: 'Choose Dockhand',
			message: 'Choose Dockhand (Gratuity will be applied to this dockhand)',
			backdropDismiss: false,
			inputs: employees.map(e => ({
				label: `${e.first_name} ${e.last_name}`,
				type: 'radio',
				value: ` ${e.employee_id}`,
			})),
			buttons: [
				{
					text: 'Start Checkin',
					cssClass: 'secondary',
					id: 'cancel-button',
					handler: value => {},
				},
			],
		});
		await alert.present();
		const { data } = await alert.onDidDismiss();
		await firstValueFrom(
			this._reservationService.assignEmployeeToReservation({
				reservationId,
				employeeId: +data.values,
			})
		);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - destroy
	 **-------------------------------------------------------------------------------------
	 */
	public destroy(): void {
		this.subscriptions.forEach(property => {
			if (property instanceof Subscription) {
				property.unsubscribe();
			}
		});
	}
}
